import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Tooltip, TooltipData } from '../../../../elements-tooltip/tooltip';
import { PriceBreakdownDiscounts } from '../../../common/items/booking-price-breakdown/PriceBreakdownDiscounts';
import { BookingPriceAdjustments } from './BookingPriceAdjustments';
import { getAddOnsTotal } from './get-add-ons-total';
import { getSpaceRentalTotal } from './get-space-rental-total';

export const BookingPriceCalculator = ({
  contracts,
  profile,
  offerPending,
  isGuestView,
  currency
}) => {
  const spaceRentalContract = (contracts || []).find(
    (c) => c.type === 'SPACE_RENTAL'
  );

  const masterContract = (contracts || []).find((c) => c.type === 'MASTER');
  const masterCalculations = (masterContract?.calculations || {})[
    isGuestView ? 'guest' : 'host'
  ];

  const adjustmentContracts = (contracts || []).filter(
    (c) => c.type === 'ADJUSTMENT'
  );

  const spaceRentalTotal = getSpaceRentalTotal(spaceRentalContract, profile);
  const addOnsTotal = getAddOnsTotal(masterContract, isGuestView);

  const discounts = (spaceRentalContract?.secondary_charges || [])
    .filter((c) => {
      if (c.type !== 'discount') {
        return false;
      }
      return (
        // dont show hosts guest discounts
        isGuestView ||
        !['GUEST_DISCOUNT', 'GUEST_REFERRAL_DISCOUNT'].includes(
          c.meta?.type || c.type
        )
      );
    })
    .map((d) => d.meta || d);

  return (
    <div data-testing-id="booking-price-calculator">
      <div className="flex space-between middle">
        <span>
          {offerPending && <span className="new-total-circle-icon" />}
          <FormattedMessage
            id="inbox.contract_calculator.space_rental"
            defaultMessage="Space rental"
          />
        </span>
        <span data-testing-id="price-calc-space-rental-amount">
          <FormattedNumber
            value={spaceRentalTotal}
            currency={currency}
            style="currency"
          />
        </span>
      </div>

      {isGuestView && masterCalculations?.us_sales_tax_space_rental > 0 && (
        <div className="flex space-between middle margin-sm-top">
          <span>
            {offerPending && <span className="new-total-circle-icon" />}
            <FormattedMessage
              id="price_table.space_rental_sales_tax"
              defaultMessage="Space rental sales tax"
            />
          </span>
          <span data-testing-id="price-calc-space-rental-tax-amount">
            <FormattedNumber
              value={masterCalculations.us_sales_tax_space_rental}
              currency={currency}
              style="currency"
            />
          </span>
        </div>
      )}

      {!!addOnsTotal && (
        <div className="flex space-between middle margin-sm-top">
          <span>
            {offerPending && <span className="new-total-circle-icon" />}
            <FormattedMessage
              id="inbox.contract_calculator.add_ons"
              defaultMessage="Add-ons"
            />
          </span>
          <span data-testing-id="price-calc-addons-amount">
            <FormattedNumber
              value={getAddOnsTotal(masterContract, isGuestView)}
              currency={currency}
              style="currency"
            />
          </span>
        </div>
      )}

      {isGuestView && masterCalculations?.us_sales_tax_add_ons > 0 && (
        <div className="flex space-between middle margin-sm-top">
          <span>
            {offerPending && <span className="new-total-circle-icon" />}
            <FormattedMessage
              id="price-breakdown.add-on-sales-tax"
              defaultMessage="Add-on sales tax"
            />
          </span>
          <span data-testing-id="price-calc-add-ons-tax-amount">
            <FormattedNumber
              value={masterCalculations.us_sales_tax_add_ons}
              currency={currency}
              style="currency"
            />
          </span>
        </div>
      )}

      {!!discounts.length && (
        <div className="margin-sm-top">
          <PriceBreakdownDiscounts discounts={discounts} currency={currency} />
        </div>
      )}

      {!!adjustmentContracts.length && (
        <div className="margin-sm-top">
          <BookingPriceAdjustments
            adjustmentContracts={adjustmentContracts}
            currency={currency}
          />
        </div>
      )}

      <div className="flex space-between middle margin-sm-top">
        <span data-testing-id="booking-price-fees-label">
          {isGuestView ? (
            <FormattedMessage
              id="inbox.contract_calculator.processing"
              defaultMessage="Processing"
            />
          ) : (
            <FormattedMessage
              id="inbox.contract_calculator.service_fee"
              defaultMessage="Service Fee"
            />
          )}
          <span className="margin-sm-left">
            <Tooltip
              tooltip={
                TooltipData[isGuestView ? 'PROCESSING_FEE' : 'SERVICE_FEE']
              }
            />
          </span>
        </span>
        <span data-testing-id="booking-price-fees-amount">
          <FormattedNumber
            value={
              isGuestView ? masterCalculations?.fee : -masterCalculations?.fee
            }
            currency={currency}
            style="currency"
          />
        </span>
      </div>

      {isGuestView &&
        masterContract?.split_payment_checkout &&
        !!masterCalculations?.split_payment_fee_amount && (
          <div className="flex space-between middle margin-sm-top">
            <span>
              <FormattedMessage
                id="inbox.price_table.part_now_part_later_fee"
                defaultMessage="Part now, part later fee"
              />
            </span>
            <span data-testing-id="booking-price-sp-fee-amount">
              <FormattedNumber
                value={masterCalculations?.split_payment_fee_amount}
                currency={currency}
                style="currency"
              />
            </span>
          </div>
        )}

      <hr className="w-100 margin-md-top margin-md-bottom" />

      <div className="flex space-between middle margin-sm-top text-gray-800 text-large heavy">
        <span data-testing-id="booking-price-total-label">
          {offerPending ? (
            <>
              <span className="new-total-circle-icon" />
              <FormattedMessage
                id="inbox.contract_calculator.new_total"
                defaultMessage="New Total"
              />
            </>
          ) : (
            <FormattedMessage
              id="inbox.contract_calculator.total"
              defaultMessage="Total"
            />
          )}
        </span>
        <span data-testing-id="booking-price-total-amount">
          <FormattedNumber
            value={
              isGuestView
                ? masterCalculations?.payment
                : masterCalculations?.payout
            }
            currency={currency}
            style="currency"
          />
        </span>
      </div>
    </div>
  );
};

BookingPriceCalculator.propTypes = {
  profile: PropTypes.object.isRequired,
  contracts: PropTypes.arrayOf(PropTypes.object).isRequired,
  currency: PropTypes.string.isRequired,
  offerPending: PropTypes.bool,
  isGuestView: PropTypes.bool
};
